import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { composerAspectRatioValues } from '../../composerConstants';
import { ComposerAspectRatioValue } from '../../composerTypes';
import type { ComposerEmbedHandle } from '../../ComposerEmbed';

export interface ComposerAspectRatioInitialState {
  aspectRatio?: ComposerAspectRatioValue;
  customWidth?: number;
  customHeight?: number;
  requestedWidth?: number;
  requestedHeight?: number;
}

interface UseComposerAspectRatioOptions {
  composerRef: RefObject<ComposerEmbedHandle>;
  initialState?: ComposerAspectRatioInitialState;
}

function roundSize(value: number): number {
  return Math.max(1, Math.round(value));
}

function getCssValue(
  aspectRatio: ComposerAspectRatioValue,
  customWidth: number,
  customHeight: number
): string | undefined {
  if (aspectRatio === ComposerAspectRatioValue.AUTO) return undefined;
  const preset = composerAspectRatioValues[aspectRatio];
  if (preset) return `${preset.width} / ${preset.height}`;
  return `${customWidth} / ${customHeight}`; // CUSTOM
}

// Keeps the bigger side's pixel value fixed and rescales the smaller side to
// match a target ratio — used whenever the ratio changes (preset/custom
// selection) but the render size shouldn't jump to arbitrary pixel values.
function resizeToRatio(
  currentWidth: number,
  currentHeight: number,
  ratioWidth: number,
  ratioHeight: number
): { width: number; height: number } {
  const big = Math.max(currentWidth, currentHeight);
  const ratioBig = Math.max(ratioWidth, ratioHeight);
  const ratioSmall = Math.min(ratioWidth, ratioHeight);
  const small = roundSize((big * ratioSmall) / ratioBig);

  return ratioWidth >= ratioHeight
    ? { width: big, height: small }
    : { width: small, height: big };
}

/**
 * aspectRatio + customWidth/customHeight only shape the letterbox frame
 * (see ComposerAspectRatioFrame) — customWidth/Height is a small ratio pair,
 * editable only in Custom, same as ModelViewerEditor's
 * aspectRatioCustomWidth/Height.
 *
 * requestedWidth/requestedHeight is a fully separate pair used only for the
 * render request payload — it does NOT drive the frame or the canvas shape.
 * It's seeded from the widget's real canvas pixels (getViewportInfo(), read
 * once the frame has settled after an aspectRatio/cssValue change) so it
 * starts in sync with what's on screen. Editing one side by hand rescales
 * the other to match the canvas's current aspect ratio; picking a preset or
 * editing Custom width/height rescales the smaller requested side while
 * pinning the bigger side's pixel value.
 */
function useComposerAspectRatio({
  composerRef,
  initialState
}: UseComposerAspectRatioOptions) {
  const [aspectRatio, setAspectRatioState] = useState<ComposerAspectRatioValue>(
    initialState?.aspectRatio ?? ComposerAspectRatioValue.AUTO
  );
  const [customWidth, setCustomWidthState] = useState(
    initialState?.customWidth ?? 1
  );
  const [customHeight, setCustomHeightState] = useState(
    initialState?.customHeight ?? 1
  );
  const [requestedWidth, setRequestedWidthState] = useState(
    initialState?.requestedWidth ?? 0
  );
  const [requestedHeight, setRequestedHeightState] = useState(
    initialState?.requestedHeight ?? 0
  );
  // Tracks whether the user has hand-edited requestedWidth/Height (or a
  // persisted value was restored) since the frame/aspect ratio last changed
  // — while true, the viewport poller below stops overwriting them,
  // regardless of aspect ratio mode (including Auto).
  const requestedTouchedRef = useRef(Boolean(initialState?.requestedWidth));

  const setAspectRatio = useCallback(
    (value: ComposerAspectRatioValue) => {
      setAspectRatioState(value);

      const preset = composerAspectRatioValues[value];
      if (preset) {
        requestedTouchedRef.current = true;
        const resized = resizeToRatio(
          requestedWidth,
          requestedHeight,
          preset.width,
          preset.height
        );
        setRequestedWidthState(resized.width);
        setRequestedHeightState(resized.height);
      } else {
        // AUTO / CUSTOM: no fixed ratio yet (Custom keeps its own
        // customWidth/Height until the user edits them) — resume tracking
        // the canvas viewport.
        requestedTouchedRef.current = false;
      }
    },
    [requestedWidth, requestedHeight]
  );

  const setCustomWidth = useCallback(
    (value: number) => {
      const nextCustomWidth = roundSize(value);
      setCustomWidthState(nextCustomWidth);
      requestedTouchedRef.current = true;
      const resized = resizeToRatio(
        requestedWidth,
        requestedHeight,
        nextCustomWidth,
        customHeight
      );
      setRequestedWidthState(resized.width);
      setRequestedHeightState(resized.height);
    },
    [requestedWidth, requestedHeight, customHeight]
  );

  const setCustomHeight = useCallback(
    (value: number) => {
      const nextCustomHeight = roundSize(value);
      setCustomHeightState(nextCustomHeight);
      requestedTouchedRef.current = true;
      const resized = resizeToRatio(
        requestedWidth,
        requestedHeight,
        customWidth,
        nextCustomHeight
      );
      setRequestedWidthState(resized.width);
      setRequestedHeightState(resized.height);
    },
    [requestedWidth, requestedHeight, customWidth]
  );

  // Manual edit of one requested side: apply it verbatim, then rescale the
  // other side to match the canvas/camera's real current aspect ratio.
  const setRequestedWidth = useCallback(
    (value: number) => {
      requestedTouchedRef.current = true;
      const nextWidth = roundSize(value);
      setRequestedWidthState(nextWidth);

      const viewportInfo = composerRef.current?.getViewportInfo();
      if (viewportInfo && viewportInfo.width > 0) {
        setRequestedHeightState(
          roundSize((nextWidth * viewportInfo.height) / viewportInfo.width)
        );
      }
    },
    [composerRef]
  );

  const setRequestedHeight = useCallback(
    (value: number) => {
      requestedTouchedRef.current = true;
      const nextHeight = roundSize(value);
      setRequestedHeightState(nextHeight);

      const viewportInfo = composerRef.current?.getViewportInfo();
      if (viewportInfo && viewportInfo.height > 0) {
        setRequestedWidthState(
          roundSize((nextHeight * viewportInfo.width) / viewportInfo.height)
        );
      }
    },
    [composerRef]
  );

  const cssValue = getCssValue(aspectRatio, customWidth, customHeight);
  const isAuto = aspectRatio === ComposerAspectRatioValue.AUTO;

  // The letterbox frame resizes the widget's mount point via CSS
  // aspect-ratio; the widget's own R3F <Canvas> (100%/100%) then resizes
  // itself to fill it. That resize is async (ResizeObserver inside the
  // widget). In every mode this is only a *seed*, never a live binding once
  // the user (or a restored persisted value) has touched
  // requestedWidth/Height — a hand-typed value is never overwritten by a
  // poll tick, in Auto included.
  //
  // Auto: no fixed frame to settle into (window resize, sidebar toggle can
  // change the canvas at any time), so this keeps polling indefinitely,
  // continuously mirroring the live canvas size until the user types over
  // it.
  useEffect(() => {
    if (!isAuto) return;

    const POLL_INTERVAL_MS = 250;

    const applyViewport = () => {
      if (requestedTouchedRef.current) return;
      const viewportInfo = composerRef.current?.getViewportInfo();
      if (!viewportInfo || viewportInfo.width <= 0 || viewportInfo.height <= 0)
        return;

      // The canvas itself is the source of truth in Auto — pin its actual
      // bigger side's pixel value and rescale the smaller side to match its
      // actual ratio, rather than carrying over a previous requested size
      // (e.g. inherited from a preset before switching back to Auto).
      const big = Math.max(viewportInfo.width, viewportInfo.height);
      const resized =
        viewportInfo.width >= viewportInfo.height
          ? {
              width: big,
              height: roundSize(
                (big * viewportInfo.height) / viewportInfo.width
              )
            }
          : {
              width: roundSize(
                (big * viewportInfo.width) / viewportInfo.height
              ),
              height: big
            };
      setRequestedWidthState(resized.width);
      setRequestedHeightState(resized.height);
    };

    const interval = window.setInterval(applyViewport, POLL_INTERVAL_MS);
    applyViewport();

    return () => window.clearInterval(interval);
  }, [isAuto, composerRef]);

  // Non-auto: the frame's cssValue just changed shape, so poll until
  // getViewportInfo() reports a stable size, seed requestedWidth/Height from
  // it once, then stop — no continuous tracking needed since the frame's
  // shape (and therefore the canvas size) doesn't change on its own between
  // aspectRatio/cssValue changes.
  useEffect(() => {
    if (isAuto || requestedTouchedRef.current) return;

    const POLL_INTERVAL_MS = 100;
    const REQUIRED_STABLE_TICKS = 3;

    let settleTicks = 0;
    let lastSeen: { width: number; height: number } | null = null;

    const applyViewport = () => {
      if (requestedTouchedRef.current) {
        window.clearInterval(interval);
        return;
      }

      const viewportInfo = composerRef.current?.getViewportInfo();
      if (!viewportInfo) return;

      const current = {
        width: roundSize(viewportInfo.width),
        height: roundSize(viewportInfo.height)
      };

      if (
        lastSeen &&
        lastSeen.width === current.width &&
        lastSeen.height === current.height
      ) {
        settleTicks += 1;
        if (settleTicks >= REQUIRED_STABLE_TICKS) {
          setRequestedWidthState(current.width);
          setRequestedHeightState(current.height);
          window.clearInterval(interval);
        }
      } else {
        settleTicks = 0;
      }
      lastSeen = current;
    };

    const interval = window.setInterval(applyViewport, POLL_INTERVAL_MS);
    applyViewport();

    return () => window.clearInterval(interval);
  }, [isAuto, cssValue, composerRef]);

  return {
    aspectRatio,
    customWidth,
    customHeight,
    cssValue,
    requestedWidth,
    requestedHeight,
    setAspectRatio,
    setCustomWidth,
    setCustomHeight,
    setRequestedWidth,
    setRequestedHeight
  };
}

export default useComposerAspectRatio;
